import { type Repub } from '@cx-modules/constants';

// Google Ad Manager (GAM) / Google Publisher Tags ad-slot configuration (NGF-6352). Single source of
// truth shared by the RR7 atlas host (apps/atlas) and atlas-astro — the per-repub/per-URL slot
// definitions, the ad-injection region gate and the GAM network code. Consumed via
// `@cx-web/feature-gam-advertising` so the slot shapes and network code live in exactly one place.
//
// Injection is gated to UK (OC) + Latam (BR/CO) + IT + ES + US + WhoScored (WS). OC and WS both
// resolve to a URL with no repub path segment (packages/utils/src/repubs.ts), so a gate that sniffs
// the repub off the URL path can't tell them apart — the caller's already-resolved `repub` (request
// identity, not the URL) is required instead.
const AD_INJECTION_ELIGIBLE_REPUBS: Repub[] = ['OC', 'BR', 'CO', 'ES', 'IT', 'US', 'WS'];

// Target-node ids referenced OUTSIDE this file — by GoogleAdUnit/AdHeaderSlot's slotRenderEnded
// handling (skyscraper takeover detection, the leaderboard's debug-console reveal) and by the
// page templates that render the `inject` skyscraper divs directly (GoogleAdUnit/AdHeaderSlot never
// render divs — see AdUnitConfiguration.injectOrCreate). Exported so every consumer shares one
// literal instead of re-typing the id string.
export const TOP_BANNER_SLOT_ID = 'apn_ad_slot_728_top_generic';
export const SKYSCRAPER_LEFT_SLOT_ID = 'apn_ad_slot_left';
export const SKYSCRAPER_RIGHT_SLOT_ID = 'apn_ad_slot_right';
export const LEADERBOARD_SLOT_ID = 'div-gpt-ad';
export const MOBILE_BOTTOM_SLOT_ID = 'apn_ad_slot_bottom_mobile';
export const MPU_SLOT_ID = 'apn_ad_slot_MPU';

const isAdInjectionEligibleRepub = (repub: Repub): boolean => AD_INJECTION_ELIGIBLE_REPUBS.includes(repub);

interface CreateAdConfiguration {
  isEnabled: boolean;
  repub: Repub;
  url: string;
}

export interface AdUnitConfiguration {
  adUnitCode: string;
  dimensions: number[] | number[][];
  injectOrCreate: 'inject' | 'create';
  /**
   * Semantic identifier for the physical page position this slot renders at — the render sites keying
   * off this need a stable handle that survives `targetNode`/CSS-selector churn, rather than
   * string-matching the DOM id itself. Optional so existing `AdUnitConfiguration` literals (fixtures/
   * tests predating this field) don't need updating just to satisfy the type; every slot
   * `createAdConfiguration` produces sets it.
   */
  placement?: 'inline-banner' | 'leaderboard' | 'mobile-sticky-bottom' | 'mpu' | 'skyscraper-left' | 'skyscraper-right';
  targetNode: string;
  targeting?: {
    position?: 'right' | 'left';
  };
}

const getAdSlotPathName = (pathName: string): string => {
  const HOMEPAGE_PATH = '/homepage';

  // OC (UK) has no repub path prefix, so its homepage request path is the bare root — normalise it
  // the same way a prefixed repub's 2-letter homepage path is normalised below.
  if (pathName === '/') {
    return HOMEPAGE_PATH;
  }

  if (/^\/[a-z]{2}\/?$/i.test(pathName)) {
    const normalised = pathName.endsWith('/') ? pathName.slice(0, -1) : pathName;
    return `${normalised}${HOMEPAGE_PATH}`;
  }

  return pathName;
};

export const createAdConfiguration = ({ isEnabled, repub, url }: CreateAdConfiguration): AdUnitConfiguration[] => {
  if (!isEnabled || !isAdInjectionEligibleRepub(repub)) {
    return [];
  }

  const requestUrl = new URL(url);
  const requestUrlPath = requestUrl?.pathname?.toLowerCase();
  const adPathName = getAdSlotPathName(requestUrlPath);

  const networkCode = '/22664628094';
  const adUnitCode = `${networkCode}${adPathName}`;

  return [
    {
      adUnitCode,
      // Desktop 728x90/970x90/970x250, Tablet 728x90, no mobile size.
      dimensions: [
        [728, 90],
        [970, 90],
        [970, 250],
      ],
      targetNode: TOP_BANNER_SLOT_ID,
      placement: 'leaderboard',
      injectOrCreate: 'create',
    },
    {
      adUnitCode,
      // Desktop 680x45/1200x45, Tablet 680x45, Mobile 340x45/375x50 — the desktop/tablet inline banner
      // (top of content) and the mobile inline banner share the identical `#div-gpt-ad` selector.
      // Rather than two ad units racing for one DOM id (invalid HTML), this is modelled as ONE
      // inline-top-of-content slot spanning every breakpoint's sizes; the render layer mounts a single
      // `#div-gpt-ad` div and GAM serves whichever creative fits the visitor's viewport.
      dimensions: [
        [680, 45],
        [1200, 45],
        [340, 45],
        [375, 50],
      ],
      targetNode: LEADERBOARD_SLOT_ID,
      placement: 'inline-banner',
      injectOrCreate: 'create',
    },
    {
      adUnitCode,
      // Desktop only. 120x600 (Standard) and 160x600 (Large) are the two standard sizes; the larger
      // takeover formats ([360,811]/[530,650]/[800,1000]) predate them and are kept for sponsored
      // takeovers, where a skyscraper is allowed to exceed the standard 600px max depth.
      dimensions: [
        [120, 600],
        [160, 600],
        [360, 811],
        [530, 650],
        [800, 1000],
      ],
      targetNode: SKYSCRAPER_LEFT_SLOT_ID,
      placement: 'skyscraper-left',
      targeting: {
        position: 'left',
      },
      injectOrCreate: 'inject',
    },
    {
      adUnitCode,
      dimensions: [
        [120, 600],
        [160, 600],
        [360, 811],
        [530, 650],
        [800, 1000],
      ],
      targetNode: SKYSCRAPER_RIGHT_SLOT_ID,
      placement: 'skyscraper-right',
      targeting: {
        position: 'right',
      },
      injectOrCreate: 'inject',
    },
    {
      adUnitCode,
      // 300x250 on both Desktop and Tablet, no mobile size. Home/Right-Rail-template-only and
      // single-column exclusion are render-site concerns, not encoded here.
      dimensions: [300, 250],
      targetNode: MPU_SLOT_ID,
      placement: 'mpu',
      injectOrCreate: 'create',
    },
    {
      adUnitCode,
      // Sticky bottom mobile banner, fixed to the viewport footer.
      dimensions: [
        [320, 50],
        [375, 50],
        [320, 100],
      ],
      targetNode: MOBILE_BOTTOM_SLOT_ID,
      placement: 'mobile-sticky-bottom',
      injectOrCreate: 'create',
    },
  ];
};
